import React, { useState, useEffect } from "react";
import "@/App.css";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import { Toaster, toast } from "sonner";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import AvisoDePrivacidad from "@/pages/AvisoDePrivacidad";
import Empresa from "@/pages/Empresa";
import ContactoPage from "@/pages/Contacto";
import {
  Menu,
  X,
  Mail,
  Linkedin,
  CheckCircle2,
  ShieldCheck,
  TrendingUp,
  ScrollText,
  Award,
  ArrowRight,
  ChevronDown,
  Building2,
  FileSpreadsheet,
  PiggyBank,
  ClipboardCheck,
  Globe2,
  Send,
  Loader2,
  HandHeart,
  Scale,
  Briefcase,
  Target,
  Handshake,
  Compass,
  Eye,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_consultores-landing-1/artifacts/zr6j82m0_ayces-01.png";

const EMAILJS = {
  serviceId: "service_1jrr2gh",
  templateId: "template_k8lbn8i",
  publicKey: "Ovbs87gpnI2MI5jDN",
};

const SERVICES = [
  "Valuaciones Actuariales NIF D-3",
  "Valuaciones Actuariales US GAAP (ASC 715)",
  "Valuaciones Actuariales IAS 19",
  "Planes de pensiones personalizados",
  "Registro en línea ante la CONSAR",
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

const stagger = {
  initial: {},
  whileInView: {},
  viewport: { once: true, margin: "-80px" },
  transition: { staggerChildren: 0.1 },
};

/* ---------------- Navigation ---------------- */
export const Navigation = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const linkFor = (hash) => (isHome ? hash : `/${hash}`);
  const links = [
    { label: "Nosotros", hash: "#nosotros" },
    { label: "Enfoques", hash: "#enfoques" },
    { label: "Servicios", hash: "#servicios" },
    { label: "Valores", hash: "#valores" },
    { label: "Contacto", hash: "#contacto" },
  ];
  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 glass-nav"
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-3 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-3"
          data-testid="nav-logo"
        >
          <img
            src={LOGO_URL}
            alt="AYCES"
            className="h-14 lg:h-16 w-auto object-contain"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.hash}
              href={linkFor(l.hash)}
              className="nav-link text-sm font-medium text-neutral-700 hover:text-brand-navy transition-colors"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:block">
          <a href={linkFor("#contacto")} data-testid="nav-cta">
            <Button className="bg-brand-navy hover:bg-brand-blue text-white rounded-full px-6 btn-shine">
              Solicitar Cotización
            </Button>
          </a>
        </div>

        <button
          className="lg:hidden p-2 text-brand-navy"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-brand-silver/40 bg-white">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.hash}
                href={linkFor(l.hash)}
                onClick={() => setOpen(false)}
                className="text-neutral-800 py-2 border-b border-neutral-100 text-sm"
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            ))}
            <a href={linkFor("#contacto")} onClick={() => setOpen(false)}>
              <Button className="bg-brand-navy hover:bg-brand-blue text-white rounded-full w-full mt-2">
                Solicitar Cotización
              </Button>
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

/* ---------------- Hero ---------------- */
const Hero = () => {
  return (
    <section
      id="inicio"
      className="relative hero-bg min-h-[100vh] flex items-center overflow-hidden grain"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -left-20 w-[400px] h-[400px] rounded-full bg-brand-blue/10 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-brand-navy/10 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-20 w-full">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-4xl"
        >
          <span className="inline-block text-xs tracking-[0.28em] uppercase text-brand-navy/80 border border-brand-navy/25 bg-white/60 px-4 py-2 rounded-full mb-8 backdrop-blur-sm">
            “ Servicios Actuariales Especializados ”
          </span>

          <h1 className="font-serif font-bold text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight mb-6">
            <span className="text-[#062479]">Precisión Actuarial</span> <span className="text-[#062479]">y</span>
            <br />
            <span className="italic text-[#2e5ac2]">Estadísticas</span>
            <br />
            <span className="text-[#2e5ac2]">Financieras</span>
            <br />
            <span className="text-[#062479]">para la Gestión de Pasivos Laborales</span>
          </h1>

          <p className="text-neutral-700 text-base sm:text-lg max-w-2xl leading-relaxed mb-10 font-light">
            Aplicamos metodologías actuariales avanzadas para el cálculo,
            análisis, proyección de pasivos laborales y planes de pensiones,
            brindando certeza financiera y cumplimiento normativo.
          </p>

          <div className="flex flex-wrap gap-4">
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center text-brand-navy/70">
        <ChevronDown className="h-10 w-10 scroll-dot" strokeWidth={3} />
      </div>
    </section>
  );
};

/* ---------------- Nosotros ---------------- */
export const Nosotros = () => {
  return (
    <section
      id="nosotros"
      className="py-24 lg:py-32 bg-white"
      data-testid="nosotros-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div {...fadeUp} className="mb-16">
          <span className="text-xs tracking-[0.28em] uppercase text-brand-blue font-medium">
            Nosotros
          </span>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-navy tracking-tight mt-4 max-w-3xl leading-[1.05]">
            Más de 30 años realizando servicios actuariales
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <motion.div {...fadeUp} className="lg:col-span-7">
            <p className="text-neutral-700 text-lg leading-relaxed mb-8">
              Somos una firma especializada en consultoría actuarial enfocada en
              la valuación de obligaciones laborales, análisis de pasivos y
              cumplimiento normativo. Integramos modelos actuariales,
              financieros y estadísticos para generar información precisa y
              confiable que respalde la toma de decisiones. Nuestro enfoque
              combina rigor técnico con entendimiento del negocio, permitiendo a
              nuestros clientes optimizar su planeación financiera y gestionar
              adecuadamente sus recursos financieros.
            </p>

            <div className="mt-10">
              <Tabs defaultValue="mision" className="w-full" data-testid="mision-vision-tabs">
                <TabsList className="bg-brand-gray/40 p-1 rounded-sm h-auto inline-flex">
                  <TabsTrigger
                    value="mision"
                    data-testid="tab-mision"
                    className="flex items-center gap-2 px-5 py-2.5 rounded-sm text-sm font-medium data-[state=active]:bg-brand-navy data-[state=active]:text-white text-brand-navy/70"
                  >
                    <Compass className="h-4 w-4" />
                    Nuestra Misión
                  </TabsTrigger>
                  <TabsTrigger
                    value="vision"
                    data-testid="tab-vision"
                    className="flex items-center gap-2 px-5 py-2.5 rounded-sm text-sm font-medium data-[state=active]:bg-brand-navy data-[state=active]:text-white text-brand-navy/70"
                  >
                    <Eye className="h-4 w-4" />
                    Nuestra Visión
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="mision" className="mt-8">
                  <div className="border-l-2 border-brand-navy pl-6">
                    <p className="text-neutral-600 text-sm leading-relaxed">
                      Proveer servicios de consultoría actuarial, valuación de
                      obligaciones laborales y análisis de pasivos, mediante la
                      aplicación de modelos actuariales, financieros y estadísticos,
                      que aseguren el cumplimiento de normativas nacionales e
                      internacionales que respalden la toma de decisiones
                      estratégicas de nuestros clientes.
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="vision" className="mt-8">
                  <div className="border-l-2 border-brand-blue pl-6">
                    <p className="text-neutral-600 text-sm leading-relaxed">
                      Consolidarnos como una firma de referencia en consultoría
                      actuarial en México, destacando el rigor técnico en nuestras
                      valuaciones, la correcta interpretación de riesgos y pasivos,
                      y la generación de información confiable que contribuya a la
                      planeación financiera de nuestros clientes.
                    </p>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </motion.div>

          <motion.div
            {...fadeUp}
            className="lg:col-span-5 relative"
            data-testid="nosotros-image"
          >
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_consultores-landing-1/artifacts/ubtgb2mt_nosotros-ayces.webp"
                alt="Equipo de profesionales actuariales"
                className="w-full h-[480px] object-cover rounded-sm"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/40 to-transparent rounded-sm" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Enfoques ---------------- */
const Enfoques = () => {
  const items = [
    {
      icon: TrendingUp,
      title: "Enfoque Estratégico",
      desc: "Transformamos datos complejos en información clara y accionable. Nuestros servicios actuariales permiten anticipar riesgos, optimizar costos laborales y fortalecer la planeación financiera de nuestros clientes.",
    },
    {
      icon: ShieldCheck,
      title: "Enfoque Normativo",
      desc: "Garantizamos que sus obligaciones en materia de pasivos laborales cumplan con estándares como NIF D-3, IAS 19 y US GAAP, brindándole tranquilidad ante auditorías y revisiones regulatorias.",
    },
    {
      icon: ScrollText,
      title: "Enfoque de Valor",
      desc: "No solo cuantificamos obligaciones, aportamos análisis estratégicos que mejoran la toma de decisiones, optimizan recursos y fortalecen la salud financiera de su empresa.",
    },
    {
      icon: ClipboardCheck,
      title: "Enfoque de Calidad",
      desc: "Nuestro equipo mantiene disponibilidad continua durante todo el año, asegurando seguimiento constante, tiempos de respuesta ágiles y soporte oportuno en cada etapa de sus necesidades actuariales.",
    },
  ];

  return (
    <section
      id="enfoques"
      className="py-24 lg:py-32 bg-[#F8F9FA] relative overflow-hidden"
      data-testid="enfoques-section"
    >
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-brand-navy/5 blur-3xl rounded-full" />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center mb-16">
          <motion.div {...fadeUp} className="lg:col-span-7">
            <span className="text-xs tracking-[0.28em] uppercase text-brand-blue font-medium">
              Enfoques
            </span>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-navy tracking-tight mt-4 leading-[1.05]">
              Nuestra diferencia
            </h2>
            <p className="text-neutral-700 text-base lg:text-lg leading-relaxed mt-6">
              Implementamos metodologías rigurosas en cada proceso de análisis,
              aplicando modelos actuariales, financieros y estadísticos bajo
              estándares profesionales y normativos nacionales e internacionales
              vigentes. Garantizamos que nuestras valuaciones y proyecciones estén
              sustentadas en supuestos sólidos, consistentes y auditables.
              Contamos con una trayectoria consolidada que respalda nuestra
              colaboración con las firmas de auditoría más reconocidas en México.
            </p>
          </motion.div>

          <motion.div
            {...fadeUp}
            className="lg:col-span-5 relative"
            data-testid="enfoques-image"
          >
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_consultores-landing-1/artifacts/wzpjpxoi_diferencia-ayces.webp"
                alt="Profesionales actuariales en reunión"
                className="w-full h-[420px] object-cover rounded-sm"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-navy/25 via-transparent to-transparent rounded-sm" />
              <div className="absolute -bottom-4 -right-4 w-32 h-32 border-2 border-brand-navy/15 rounded-sm pointer-events-none hidden md:block" />
            </div>
          </motion.div>
        </div>

        <motion.div
          {...stagger}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {items.map((it, i) => (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="svc-card bg-white border border-brand-silver/40 p-7 lg:p-8 flex flex-col h-full"
              data-testid={`enfoque-card-${i}`}
            >
              <div className="icon-ring h-12 w-12 rounded-sm flex items-center justify-center mb-6">
                <it.icon className="h-5 w-5 text-white" />
              </div>
              <h3 className="font-sans font-bold text-xl text-brand-navy mb-3 leading-tight">
                {it.title}
              </h3>
              <p className="text-neutral-600 text-sm leading-relaxed">
                {it.desc}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

/* ---------------- Servicios ---------------- */
const Servicios = () => {
  const valuations = [
    {
      icon: FileSpreadsheet,
      tag: "NIF D-3",
      title: "NIF D-3",
      desc: 'La NIF D-3, "Beneficios a los empleados", tiene por objeto establecer las normas de valuación, presentación y revelación para el reconocimiento inicial y posterior en los estados financieros de una entidad de los beneficios que esta otorga a sus empleados.',
    },
    {
      icon: Globe2,
      tag: "ASC 715",
      title: "US GAAP (ASC 715)",
      desc: "Norma contable de EE.UU. que regula cómo las empresas reconocen y valoran los costos y obligaciones de los beneficios como prima de antigüedad y planes de pensiones, su cálculo depende de los hechos y circunstancias particulares de cada caso siempre y cuando se cumpla con la Ley.",
    },
    {
      icon: Building2,
      tag: "NIC 19",
      title: "IAS 19",
      desc: "Es el estándar emitido por el IASB (NIC 19 - Norma Internacional de Contabilidad 19) que tiene por objeto el tratamiento contable y la revelación de información financiera sobre los beneficios a los empleados. Su principio fundamental exige reconocer el costo de estos beneficios en el periodo en que el empleado presta el servicio.",
    },
  ];

  const additional = [
    {
      icon: PiggyBank,
      title: "Planes de pensiones personalizados",
      desc: "Diseñamos planes de pensiones a la medida de su empresa, alineados a sus objetivos financieros y necesidades laborales, brindando seguridad y beneficios sostenibles para sus colaboradores.",
    },
    {
      icon: ClipboardCheck,
      title: "Registro en línea ante la CONSAR",
      desc: "Gestionamos el registro digital de sus planes de pensiones de forma ágil y segura, asegurando cumplimiento normativo y simplificando cada etapa del proceso.",
    },
  ];

  return (
    <section
      id="servicios"
      className="py-24 lg:py-32 bg-white"
      data-testid="servicios-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div {...fadeUp} className="mb-16 max-w-3xl">
          <span className="text-xs tracking-[0.28em] uppercase text-brand-blue font-medium">
            Nuestros Servicios
          </span>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-navy tracking-tight mt-4 leading-[1.05]">
            Valuaciones Actuariales de pasivos laborales
          </h2>
        </motion.div>

        <motion.div
          {...stagger}
          className="grid md:grid-cols-3 gap-6 mb-10"
        >
          {valuations.map((s, i) => (
            <motion.article
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="svc-card group relative bg-white border border-brand-silver/50 p-8 lg:p-10 flex flex-col h-full"
              data-testid={`servicio-card-${i}`}
            >
              <div className="flex items-center justify-between mb-8">
                <div className="icon-ring h-14 w-14 rounded-sm flex items-center justify-center">
                  <s.icon className="h-6 w-6 text-white" />
                </div>
                <span className="text-[10px] tracking-[0.22em] uppercase text-brand-blue bg-brand-gray/60 px-3 py-1.5">
                  {s.tag}
                </span>
              </div>
              <h3 className="font-sans font-bold text-2xl lg:text-3xl text-brand-navy mb-4 leading-tight">
                {s.title}
              </h3>
              <p className="text-neutral-600 text-sm leading-relaxed">
                {s.desc}
              </p>
            </motion.article>
          ))}
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {additional.map((s, i) => (
            <motion.article
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="svc-card bg-brand-navy text-white p-8 lg:p-10 flex flex-col lg:flex-row gap-6 items-start"
              data-testid={`servicio-extra-${i}`}
            >
              <div className="bg-white/10 h-14 w-14 rounded-sm flex items-center justify-center flex-shrink-0">
                <s.icon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-sans font-bold text-2xl text-white mb-3 leading-tight">
                  {s.title}
                </h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  {s.desc}
                </p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Valores ---------------- */
const Valores = () => {
  const values = [
    { label: "Honestidad", Icon: HandHeart },
    { label: "Ética", Icon: Scale },
    { label: "Profesionalismo", Icon: Briefcase },
    { label: "Responsabilidad", Icon: Target },
    { label: "Compromiso", Icon: Handshake },
  ];

  return (
    <section
      id="valores"
      className="py-24 lg:py-32 bg-[#F8F9FA] border-y border-brand-silver/40"
      data-testid="valores-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div {...fadeUp} className="mb-16 max-w-3xl">
          <span className="text-xs tracking-[0.28em] uppercase text-brand-blue font-medium">
            Nuestros Valores
          </span>
          <h2 className="font-serif text-4xl sm:text-5xl text-brand-navy tracking-tight mt-4 leading-[1.05]">
            Los principios que respaldan cada análisis
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: values list */}
          <motion.ul {...stagger} className="space-y-4">
            {values.map((v, i) => (
              <motion.li
                key={v.label}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="svc-card flex items-center gap-5 bg-white border border-brand-silver/50 px-6 py-5"
                data-testid={`valor-item-${i}`}
              >
                <div className="icon-ring h-12 w-12 rounded-sm flex items-center justify-center flex-shrink-0">
                  <v.Icon className="h-5 w-5 text-white" />
                </div>
                <span className="font-serif text-2xl sm:text-3xl text-brand-navy leading-none">
                  {v.label}
                </span>
              </motion.li>
            ))}
          </motion.ul>

          {/* Right: certification */}
          <motion.div
            {...fadeUp}
            className="relative overflow-hidden border border-brand-silver/50 min-h-[280px] flex items-center"
            data-testid="certificacion-card"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_consultores-landing-1/artifacts/fxgmi81l_certificacion-actuarial.webp"
              alt="Certificación Actuarial"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-white/85 via-white/55 to-white/25" />
            <div className="relative flex items-center gap-5 px-6 py-8 z-10">
              <div className="icon-ring h-12 w-12 rounded-sm flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="h-5 w-5 text-white" />
              </div>
              <span className="font-serif text-2xl sm:text-3xl text-brand-navy leading-none">
                Certificación Actuarial
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Contacto ---------------- */
export const Contacto = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [selectedServices, setSelectedServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const toggleService = (svc) => {
    setSelectedServices((prev) =>
      prev.includes(svc) ? prev.filter((s) => s !== svc) : [...prev, svc]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Por favor completa los campos requeridos.");
      return;
    }
    if (selectedServices.length === 0) {
      toast.error("Selecciona al menos un servicio que requieras.");
      return;
    }

    setSubmitting(true);
    try {
      const templateParams = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || "No proporcionado",
        services: selectedServices.join(", "),
        message: formData.message,
        // common alternative param names some EmailJS templates use
        from_name: formData.name,
        from_email: formData.email,
        reply_to: formData.email,
        title: `Solicitud AYCES - ${selectedServices.length} servicio(s)`,
      };

      await emailjs.send(
        EMAILJS.serviceId,
        EMAILJS.templateId,
        templateParams,
        { publicKey: EMAILJS.publicKey }
      );

      toast.success(
        "¡Mensaje enviado! Nos pondremos en contacto contigo a la brevedad."
      );
      setFormData({ name: "", email: "", phone: "", message: "" });
      setSelectedServices([]);
    } catch (err) {
      console.error("EmailJS error:", err);
      toast.error(
        "No se pudo enviar el mensaje. Inténtalo de nuevo o escríbenos a contacto@ayces.com.mx"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contacto"
      className="py-24 lg:py-32 bg-white"
      data-testid="contacto-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <span className="text-xs tracking-[0.28em] uppercase text-brand-blue font-medium">
              Contacto
            </span>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-navy tracking-tight mt-4 leading-[1.05] mb-8">
              Contáctenos
            </h2>
            <p className="text-neutral-600 text-base leading-relaxed mb-10">
              Con gusto le brindaremos la información que necesita sobre nuestros servicios.
            </p>

            <div className="space-y-5">
              <a
                href="mailto:contacto@ayces.com.mx"
                className="flex items-start gap-4 group"
                data-testid="contact-email"
              >
                <div className="h-12 w-12 bg-brand-gray/60 rounded-sm flex items-center justify-center flex-shrink-0 group-hover:bg-brand-navy group-hover:text-white transition-colors">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-brand-blue mb-1">
                    Correo
                  </div>
                  <div className="text-brand-navy font-medium">
                    contacto@ayces.com.mx
                  </div>
                </div>
              </a>

              <a
                href="https://www.linkedin.com/company/actuarios-y-consultores-estrat%C3%A9gicos-sc/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 group"
                data-testid="contact-linkedin"
              >
                <div className="h-12 w-12 bg-brand-gray/60 rounded-sm flex items-center justify-center flex-shrink-0 group-hover:bg-brand-navy group-hover:text-white transition-colors">
                  <Linkedin className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-brand-blue mb-1">
                    Redes Sociales
                  </div>
                  <div className="text-brand-navy font-medium">
                    Actuarios y Consultores Estratégicos SC
                  </div>
                </div>
              </a>
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="lg:col-span-7">
            <Card className="border border-brand-silver/50 rounded-sm p-6 sm:p-10 shadow-[0_24px_60px_-30px_rgba(6,36,121,0.25)]">
              <form
                onSubmit={handleSubmit}
                className="space-y-6"
                data-testid="contact-form"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label
                      htmlFor="name"
                      className="text-xs tracking-[0.18em] uppercase text-brand-navy"
                    >
                      Nombre *
                    </Label>
                    <Input
                      id="name"
                      data-testid="input-name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="Tu nombre completo"
                      className="h-12 rounded-sm border-brand-silver/60 focus-visible:ring-brand-navy"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="email"
                      className="text-xs tracking-[0.18em] uppercase text-brand-navy"
                    >
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="input-email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      placeholder="correo@empresa.com"
                      className="h-12 rounded-sm border-brand-silver/60 focus-visible:ring-brand-navy"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="phone"
                    className="text-xs tracking-[0.18em] uppercase text-brand-navy"
                  >
                    Teléfono
                  </Label>
                  <Input
                    id="phone"
                    data-testid="input-phone"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    placeholder="+52 ..."
                    className="h-12 rounded-sm border-brand-silver/60 focus-visible:ring-brand-navy"
                  />
                </div>

                <div className="space-y-3">
                  <Label className="text-xs tracking-[0.18em] uppercase text-brand-navy">
                    Servicio que requiere * (selecciona uno o más)
                  </Label>
                  <div
                    className="grid sm:grid-cols-2 gap-3 mt-2"
                    data-testid="services-checkboxes"
                  >
                    {SERVICES.map((svc, idx) => {
                      const checked = selectedServices.includes(svc);
                      return (
                        <label
                          key={svc}
                          htmlFor={`svc-${idx}`}
                          className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${
                            checked
                              ? "border-brand-navy bg-brand-navy/5"
                              : "border-brand-silver/50 hover:border-brand-blue"
                          }`}
                          data-testid={`service-option-${idx}`}
                        >
                          <Checkbox
                            id={`svc-${idx}`}
                            checked={checked}
                            onCheckedChange={() => toggleService(svc)}
                            className="mt-0.5 data-[state=checked]:bg-brand-navy data-[state=checked]:border-brand-navy"
                          />
                          <span className="text-sm text-neutral-800 leading-snug">
                            {svc}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="message"
                    className="text-xs tracking-[0.18em] uppercase text-brand-navy"
                  >
                    Mensaje *
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="input-message"
                    rows={5}
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    placeholder="Cuéntanos brevemente sobre tu necesidad..."
                    className="rounded-sm border-brand-silver/60 focus-visible:ring-brand-navy resize-none"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-contact-btn"
                  className="w-full h-14 bg-brand-navy hover:bg-brand-blue text-white rounded-sm text-sm tracking-[0.18em] uppercase btn-shine"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      Enviar mensaje
                      <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>

                <p className="text-xs text-neutral-500 text-center">
                  Al enviar este formulario aceptas que AYCES utilice tus datos
                  para responder a tu solicitud.
                </p>
              </form>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Footer ---------------- */
export const Footer = () => {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const hrefFor = (hash) => (isHome ? hash : `/${hash}`);
  const navItems = [
    { l: "Nosotros", h: "#nosotros" },
    { l: "Enfoques", h: "#enfoques" },
    { l: "Servicios", h: "#servicios" },
    { l: "Valores", h: "#valores" },
    { l: "Contacto", h: "#contacto" },
  ];
  return (
    <footer
      className="bg-brand-navy text-white py-8"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="bg-white p-2 rounded-sm">
              <img
                src={LOGO_URL}
                alt="AYCES"
                className="h-12 w-auto object-contain"
              />
            </div>
            <p className="text-white/70 text-xs leading-snug max-w-xs hidden md:block">
              Actuarios y Consultores Estratégicos SC
            </p>
          </div>

          <nav className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            {navItems.map((n) => (
              <a
                key={n.h}
                href={hrefFor(n.h)}
                className="text-white/80 hover:text-white nav-link"
              >
                {n.l}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <a
              href="mailto:contacto@ayces.com.mx"
              className="flex items-center gap-2 text-white/80 hover:text-white text-sm"
              data-testid="footer-email"
            >
              <Mail className="h-4 w-4" />
              <span className="hidden sm:inline">contacto@ayces.com.mx</span>
            </a>
            <a
              href="https://www.linkedin.com/company/actuarios-y-consultores-estrat%C3%A9gicos-sc/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white/80 hover:text-white text-sm"
              data-testid="footer-linkedin"
              aria-label="LinkedIn"
            >
              <Linkedin className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div className="section-line opacity-20 my-5" />

        <div className="flex flex-col sm:flex-row justify-between gap-2 text-[11px] text-white/60">
          <div>
            © {new Date().getFullYear()} Actuarios y Consultores Estratégicos SC. Todos los derechos reservados.
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/aviso-de-privacidad"
              className="text-white/70 hover:text-white underline-offset-4 hover:underline"
              data-testid="footer-aviso-privacidad"
            >
              Aviso de Privacidad
            </Link>
            <span className="tracking-[0.18em] uppercase">
              NIF D-3 · IAS 19 · US GAAP
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

const HomePage = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <>
      <PageSEO
        title="AYCES | Estudios Actuariales y Valuación de Pasivos Laborales en México"
        description="Despacho actuarial en México. Estudios actuariales, valuación de pasivos laborales, cálculo actuarial de pensiones y cumplimiento NIF D-3, IAS 19 y US GAAP. Más de 30 años de experiencia."
        path="/"
      />
      <Navigation />
      <main>
        <Hero />
        <Nosotros />
        <Enfoques />
        <Servicios />
        <Valores />
        <Contacto />
      </main>
      <Footer />
    </>
  );
};

/* ---------------- App ---------------- */
const SEO_KEYWORDS =
  "Estudio actuarial, Estudios actuariales, despachos actuariales, consultor actuarial, actuario México, estudio actuarial pensiones, NIF D-3, valuación actuarial, calculo actuarial, cálculos de pasivos laborales, estudio actuarial de pasivos laborales, estudio actuarial de pensiones, valuación actuarial de pasivos laborales, cálculos actuariales pensiones, IAS 19, US GAAP, ASC 715, CONSAR";

export const PageSEO = ({ title, description, path }) => (
  <Helmet>
    <title>{title}</title>
    <meta name="description" content={description} />
    <meta name="keywords" content={SEO_KEYWORDS} />
    <link rel="canonical" href={`https://ayces.com.mx${path}`} />
    <meta property="og:title" content={title} />
    <meta property="og:description" content={description} />
    <meta property="og:url" content={`https://ayces.com.mx${path}`} />
    <meta name="twitter:title" content={title} />
    <meta name="twitter:description" content={description} />
  </Helmet>
);

function App() {
  return (
    <HelmetProvider>
      <div className="App font-sans bg-white text-neutral-900">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/aviso-de-privacidad" element={<AvisoDePrivacidad />} />
          <Route path="/empresa" element={<Empresa />} />
          <Route path="/empresa/" element={<Empresa />} />
          <Route path="/contacto" element={<ContactoPage />} />
          <Route path="/contacto/" element={<ContactoPage />} />
        </Routes>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
